import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildReverseLookup,
  scanDOMForTranslations,
  TranslationMatch,
} from "./parser";

const SCAN_DEBOUNCE_MS = 250;

type UseTranslationScannerOptions = {
  isOn: boolean;
  selectedMatch: TranslationMatch | null;
  t: (key: string) => string;
};

type UseTranslationScannerReturn = {
  matches: TranslationMatch[];
  scanDOM: () => void;
};

export const useTranslationScanner = ({
  isOn,
  selectedMatch,
  t,
}: UseTranslationScannerOptions): UseTranslationScannerReturn => {
  const [matches, setMatches] = useState<TranslationMatch[]>([]);
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reverseLookup = useMemo(() => buildReverseLookup(t), [t]);

  const scanDOM = useCallback(() => {
    const foundMatches = scanDOMForTranslations(reverseLookup);
    setMatches(foundMatches);
  }, [reverseLookup]);

  useEffect(() => {
    if (!isOn || selectedMatch) {
      if (!isOn) setMatches([]);
      return;
    }

    scanDOM();

    const debouncedScan = () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      scanTimeoutRef.current = setTimeout(scanDOM, SCAN_DEBOUNCE_MS);
    };

    window.addEventListener("scroll", debouncedScan, true);
    window.addEventListener("resize", debouncedScan);

    const observer = new MutationObserver(debouncedScan);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      window.removeEventListener("scroll", debouncedScan, true);
      window.removeEventListener("resize", debouncedScan);
      observer.disconnect();
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, [isOn, selectedMatch, scanDOM]);

  return { matches, scanDOM };
};
